import { useCallback, useEffect, useMemo, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
} from '@mui/material';

import { addOrganizations, patchOrganization } from 'api';
import { Organization, AssociatedEntity } from 'components/Labels';
import { getErrorDetail } from 'helpers';
import { useCandidateAdminContext } from 'hooks';
import { useSnackContext } from 'hooks/useSnackContext';
import { OrganizationVerboseProps } from 'types';
import { Option } from 'types/Form';

export type OrganizationAddProps = {
  org_id: number;
  organization: OrganizationVerboseProps;
  setOrganization: React.Dispatch<
    React.SetStateAction<OrganizationVerboseProps>
  >;
};

export const ChildEdit: React.FC<OrganizationAddProps> = (props) => {
  const { org_id, setOrganization, organization } = props;
  const [selectedChildOptions, setSelectedChildOptions] = useState<
    Array<Option>
  >([]);
  const { children } = organization;

  const { organizations, organizationArray } = useCandidateAdminContext();
  const [selectedParentOption, setSelectedParentOption] =
    useState<Option | null>(null);
  const { setSnack } = useSnackContext();
  const selectedOrgIds = useMemo(() => {
    return selectedChildOptions.map((option) => option.value);
  }, [selectedChildOptions]);
  const childIds = useMemo(() => {
    return children.map((child) => child.id);
  }, [children]);

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const availableAsParents = useMemo(() => {
    return (
      organizationArray
        // remove target org
        .filter((org) => org.id !== org_id)
        // remove orgs that have a parent
        .filter((org) => !org.parent_id)
        //  remove all orgs already a child of org in question
        .filter((org) => !childIds.includes(org.id))
    );
  }, [organizationArray, org_id, childIds]);

  const availableEntityOptions = useMemo(() => {
    return (
      availableAsParents
        // convert to option
        .filter(({ name, id }) => name && id)
        .map((org) => {
          return { label: org.name, value: org.id };
        })
        // sort alphabetically
        .sort((a, b) => a.label.localeCompare(b.label))
    );
  }, [availableAsParents]);

  const availableAsParentsOptions = useMemo(() => {
    return (
      availableAsParents
        // filter out orgs without name & id
        .filter(({ name, id }) => name && id)
        .map((org) => {
          return { label: org.name, value: org.id, key: org.id };
        })
        // sort alphabetically
        .sort((a, b) => a.label.localeCompare(b.label))
    );
  }, [availableAsParents]);

  const [submitting, setSubmitting] = useState(false);

  const disabled = useMemo(() => {
    return selectedChildOptions.length === 0 && !selectedParentOption;
  }, [selectedChildOptions, selectedParentOption]);

  const handleEditOrgs = useCallback(async () => {
    setSubmitting(true);
    try {
      if (selectedChildOptions.length) {
        const success = await addOrganizations({
          org_id: org_id,
          child_ids: selectedChildOptions.map((org) => org.value as number),
        });
        if (success) {
          // const newOrgs = organizations.filter((org) =>
          //   selectedOrgIds.includes(org.id),
          // );
          const newOrgs = selectedOrgIds.map((id) => organizations[id]);
          setOrganization((prevOrg) => {
            return { ...prevOrg, children: [...prevOrg.children, ...newOrgs] };
          });
          setSelectedChildOptions([]);
        }
      } else if (selectedParentOption) {
        const { data } = await patchOrganization(org_id, {
          parent_id: selectedParentOption.value as number,
          children: [],
        });
        setOrganization(data);
        setSelectedChildOptions([]);
      }
    } catch (e) {
      setSnack((props) => ({
        ...props,
        severity: 'error',
        open: true,
        message: getErrorDetail({
          error: e,
          fallback: 'Failed To Edit Organization',
        }),
        error: e as Error,
      }));
    } finally {
      mounted && setSubmitting(false);
    }
  }, [
    mounted,
    org_id,
    organizations,
    selectedChildOptions,
    selectedOrgIds,
    selectedParentOption,
    setOrganization,
    setSnack,
  ]);

  return (
    <>
      <Typography variant='body1'>
        Add employers to make them <AssociatedEntity /> for this{' '}
        <Organization />
      </Typography>
      <Autocomplete
        multiple
        value={selectedChildOptions}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        onChange={(e, options) => {
          if (selectedParentOption) {
            setSelectedParentOption(null);
          }
          setSelectedChildOptions(options);
        }}
        renderInput={(params) => (
          <TextField {...params} label='Search Groups' variant='standard' />
        )}
        renderOption={(props, option) => {
          return (
            <li {...props} key={option.value}>
              {option.label}
            </li>
          );
        }}
        groupBy={(option) => option.label.charAt(0)}
        options={availableEntityOptions as Array<Option>}
      />
      <Typography sx={{ mt: 6, mb: 6, textAlign: 'center' }} variant='h5'>
        OR
      </Typography>
      <Typography variant='body1'>
        Convert this <Organization /> to an <AssociatedEntity /> by assigning it
        under another <Organization />.
      </Typography>
      <Autocomplete
        value={selectedParentOption}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        onChange={useCallback(
          (_, options) => {
            if (selectedChildOptions.length > 0) {
              setSelectedChildOptions([]);
            }
            setSelectedParentOption(options);
          },
          [selectedChildOptions.length],
        )}
        renderOption={(props, option) => {
          return (
            <li {...props} key={option.value}>
              {option.label}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField {...params} label='Search Groups' variant='standard' />
        )}
        groupBy={(option) => option.label.charAt(0)}
        options={availableAsParentsOptions as Array<Option>}
      />
      <Box
        sx={{
          mt: 1,
          display: 'flex',
          flexFlow: 'row',
        }}
      >
        <Button
          disabled={disabled}
          sx={{ marginLeft: 'auto' }}
          onClick={() => {
            setSelectedParentOption(null);
            setSelectedChildOptions([]);
          }}
          variant='text'
        >
          CANCEL
        </Button>

        <LoadingButton
          type='submit'
          loading={submitting}
          disabled={disabled}
          onClick={handleEditOrgs}
          variant='contained'
          sx={{ ml: 2 }}
        >
          ADD
        </LoadingButton>
      </Box>
    </>
  );
};
